import { useState, useEffect, useCallback, useRef } from 'react';
import GamificationServices from '../services/GamificationServices';

export const useGamification = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEligible, setIsEligible] = useState(null);
  
  const dataRef = useRef(null);
  const lastFetchRef = useRef(null);

  const fetchDashboard = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    
    if (!forceRefresh && dataRef.current && lastFetchRef.current && (now - lastFetchRef.current < 30000)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await GamificationServices.getDashboard();
      
      if (result.success) {
        const dashboardData = result.data;
        setData(dashboardData);
        setIsEligible(dashboardData.profile?.is_eligible || false);
        dataRef.current = dashboardData;
        lastFetchRef.current = now;
        
        const levelUpHeader = dashboardData.profile?.level_up_info;
        if (levelUpHeader && levelUpHeader.level_up) {
          GamificationServices.showLevelUpModal(levelUpHeader);
        }
        
      } else if (result.error === 'not_eligible') {
        setIsEligible(false);
        setData(null);
      } else {
        setError(result);
        setIsEligible(null);
      }
    } catch (err) {
      console.error('Error in fetchDashboard:', err);
      setError({
        error: 'api_error',
        message: 'Erro inesperado ao carregar gamificação'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(() => {
    return fetchDashboard(true);
  }, [fetchDashboard]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    data,
    loading,
    error,
    isEligible,
    refresh: refreshData,
    fetchDashboard
  };
};

export const useLeaderboard = (params = {}) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await GamificationServices.getLeaderboard(params);
      
      if (result.success) {
        setLeaderboard(result.data.results || []);
      } else {
        setError(result);
        setLeaderboard([]);
      }
    } catch (err) {
      console.error('Error in fetchLeaderboard:', err);
      setError({
        error: 'api_error',
        message: 'Erro ao carregar ranking'
      });
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return {
    leaderboard,
    loading,
    error,
    refresh: fetchLeaderboard
  };
};

export const useAchievements = (includeLocked = true) => {
  const [achievements, setAchievements] = useState({
    unlocked: [],
    locked: [],
    unlocked_count: 0,
    locked_count: 0,
    total_available: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAchievements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await GamificationServices.getAchievements(includeLocked);
      
      if (result.success) {
        setAchievements(result.data);
      } else {
        setError(result);
      }
    } catch (err) {
      console.error('Error in fetchAchievements:', err);
      setError({
        error: 'api_error',
        message: 'Erro ao carregar conquistas'
      });
    } finally {
      setLoading(false);
    }
  }, [includeLocked]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  return {
    achievements,
    loading,
    error,
    refresh: fetchAchievements
  };
};

export const useXpHistory = (params = {}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await GamificationServices.getXpHistory(params);
      
      if (result.success) {
        setHistory(result.data);
      } else {
        setError(result);
        setHistory([]);
      }
    } catch (err) {
      console.error('Error in fetchHistory:', err);
      setError({
        error: 'api_error',
        message: 'Erro ao carregar histórico'
      });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    loading,
    error,
    refresh: fetchHistory
  };
};

export const useGamificationHeaders = () => {
  const [headers, setHeaders] = useState({});
  
  useEffect(() => {
    const interceptor = (response) => {
      const gamificationHeaders = {};
      
      const headerKeys = [
        'X-Gamification-Level',
        'X-Gamification-XP',
        'X-Gamification-Tier',
        'X-Gamification-Level-Up',
        'X-Gamification-New-Level'
      ];
      
      headerKeys.forEach(key => {
        if (response.headers[key.toLowerCase()]) {
          gamificationHeaders[key] = response.headers[key.toLowerCase()];
        }
      });
      
      if (Object.keys(gamificationHeaders).length > 0) {
        setHeaders(prev => ({ ...prev, ...gamificationHeaders }));
        
        if (gamificationHeaders['X-Gamification-Level-Up'] === 'true') {
          const levelUpData = {
            new_level: parseInt(gamificationHeaders['X-Gamification-New-Level']),
            tier_info: { tier: gamificationHeaders['X-Gamification-Tier'] },
            xp_earned: 0
          };
          
          GamificationServices.showLevelUpModal(levelUpData);
        }
      }
      
      return response;
    };

    return () => {};
  }, []);

  return headers;
};